import React, { useEffect, useState } from 'react';
import { apiFetch, errorText } from '../shared/api';
import SentRequestsTable from './SentRequestsTable';
import Spinner from '../shared/Spinner';
import LoadFailure from '../shared/LoadFailure';

const SEARCH_DEBOUNCE_MS = 300;

export default function SentRequestsModal({ endpoint, timezone, onClose, onToast }) {
    const [search, setSearch] = useState('');
    // One object so a debounced search and its page reset land in a single render.
    const [query, setQuery] = useState({ q: '', type: '', page: 1 });
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagerDirection, setPagerDirection] = useState(null);
    const [failed, setFailed] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (search === query.q) {
            return undefined;
        }
        const timer = setTimeout(() => {
            setPagerDirection(null);
            setQuery((previous) => ({ ...previous, q: search, page: 1 }));
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [search, query.q]);

    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams({ page: query.page });
        if (query.q) {
            params.set('q', query.q);
        }
        if (query.type) {
            params.set('type', query.type);
        }

        const fail = (text) => {
            setRows([]);
            setMeta(null);
            setFailed(true);
            onToast({ type: 'error', text });
        };

        setLoading(true);
        setFailed(false);
        apiFetch(`${endpoint}?${params}`)
            .then(({ status, json }) => {
                if (cancelled) {
                    return;
                }
                if (status >= 200 && status < 300 && Array.isArray(json?.data)) {
                    setRows(json.data);
                    setMeta(json.meta || null);
                    setFailed(false);
                } else {
                    console.error('SentRequestsModal: failed to load sent requests', status, json);
                    fail(errorText(json, __('Something went wrong. Please try again.')));
                }
                setLoading(false);
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                console.error(error);
                fail(__('Something went wrong. Please try again.'));
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // onToast is a fresh closure every parent render; refetching on it would loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, query, retryCount]);

    const handleTypeChange = (event) => {
        const nextType = event.target.value;
        setPagerDirection(null);
        setQuery((previous) => ({ ...previous, type: nextType, page: 1 }));
    };

    useEffect(() => {
        if (!loading) setPagerDirection(null);
    }, [loading]);

    const currentPage = meta?.current_page ?? query.page;
    const lastPage = meta?.last_page ?? 1;
    const hasFilter = query.q !== '' || query.type !== '';

    return (
        <div role="dialog" aria-label={__('Sent requests')} className="modal d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl demo-sent-requests-dialog" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h4 className="modal-title">{__('Sent requests')}</h4>
                    </div>
                    <div className="modal-body">
                        <div className="form-row mb-3">
                            <div className="col-sm-8 mb-2 mb-sm-0">
                                <div className="input-group">
                                    <input
                                        type="search"
                                        className="form-control"
                                        aria-label={__('Search sent requests')}
                                        placeholder={__('Search by name or phone')}
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                    />
                                    {loading && rows.length > 0 &&
                                        <div className="input-group-append">
                                            <span className="input-group-text">
                                                <Spinner label={__('Loading sent requests...')} />
                                            </span>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className="col-sm-4">
                                <select
                                    className="form-control"
                                    aria-label={__('Filter by type')}
                                    value={query.type}
                                    onChange={handleTypeChange}
                                >
                                    <option value="">{__('All types')}</option>
                                    <option value="rating">{__('1thru5')}</option>
                                    <option value="testimonial">{__('Testimonial')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="demo-sent-requests-modal-table">
                            {rows.length > 0 && <SentRequestsTable requests={rows} timezone={timezone} />}
                            {loading && rows.length === 0 &&
                                <>
                                    <Spinner block label={__('Loading sent requests...')} />
                                    <p className="text-muted text-center">{__('Loading...')}</p>
                                </>
                            }
                            {!loading && failed &&
                                <LoadFailure message={__('Could not load sent requests.')} onRetry={() => { setPagerDirection(null); setRetryCount((count) => count + 1); }} />
                            }
                            {!loading && !failed && rows.length === 0 &&
                                <p className="text-muted">{hasFilter ? __('No requests found.') : __('No requests sent yet.')}</p>
                            }
                        </div>

                        <div className="demo-sent-requests-pager d-flex flex-wrap align-items-center justify-content-center mt-3">
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                aria-label={__('Previous')}
                                title={__('Previous')}
                                disabled={loading || currentPage <= 1}
                                onClick={() => { setPagerDirection('previous'); setQuery((previous) => ({ ...previous, page: currentPage - 1 })); }}
                            >
                                {loading && pagerDirection === 'previous'
                                    ? <Spinner label={__('Loading the previous page')} />
                                    : <i className="fas fa-chevron-left" aria-hidden="true" />}
                            </button>
                            {/* meta is null during the first load and after a failure, so the counter would read "Page N of 1". */}
                            <span className="text-muted mx-3">{!meta ? '' : __('Page :current of :last', { ':current': currentPage, ':last': lastPage })}</span>
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                aria-label={__('Next')}
                                title={__('Next')}
                                disabled={loading || currentPage >= lastPage}
                                onClick={() => { setPagerDirection('next'); setQuery((previous) => ({ ...previous, page: currentPage + 1 })); }}
                            >
                                {loading && pagerDirection === 'next'
                                    ? <Spinner label={__('Loading the next page')} />
                                    : <i className="fas fa-chevron-right" aria-hidden="true" />}
                            </button>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {__('Close')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
